import { useState, useEffect } from 'react';
import api, { hardeningAPI, reportsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Loader2, Shield, RefreshCw, CheckCircle, AlertTriangle, XCircle,
  Activity, Globe, Zap, Users,
} from 'lucide-react';
import { toast } from 'sonner';

export default function ProductionMonitorPage() {
  const [monitor, setMonitor] = useState(null);
  const [reputation, setReputation] = useState(null);
  const [delivReport, setDelivReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [mRes, rRes, dRes] = await Promise.all([
        hardeningAPI.productionMonitor(),
        hardeningAPI.reputationSummary(),
        reportsAPI.deliverability(),
      ]);
      setMonitor(mRes.data);
      setReputation(rRes.data);
      setDelivReport(dRes.data);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, []);

  const handleSnapshot = async () => {
    const r = await hardeningAPI.reputationSnapshot();
    toast.success(`${r.data.snapshots} domain snapshot alindi`);
  };

  const handleRecovery = async () => {
    const r = await hardeningAPI.recoveryCheck();
    toast.success(`${r.data.recovered} domain recover edildi`);
    load();
  };

  const handleSyncStats = async () => {
    toast.info('Veriler senkronize ediliyor...');
    try {
      const r = await api.post('/backfill/sync-stats');
      toast.success(`Senkronize edildi: ${r.data.events_processed} event, ${r.data.campaigns_updated} kampanya, ${r.data.domains_updated} domain guncellendi`);
      load();
    } catch (e) { toast.error('Senkronizasyon hatasi'); }
  };

  const handleSyncSendGrid = async () => {
    toast.info('SendGrid suppression listesi cekiliyor...');
    try {
      const r = await api.post('/sendgrid/sync-suppressions');
      toast.success(`SendGrid sync: ${r.data.added} yeni eklendi, toplam ${r.data.total_suppressed} engelli`);
      load();
    } catch (e) { toast.error('SendGrid sync hatasi'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  const m = monitor || {};
  const r = reputation || {};
  const d = delivReport || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-heading font-bold">Production Monitor</h1><p className="text-muted-foreground mt-1">Canli sistem durumu</p></div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleSyncStats}><RefreshCw className="w-4 h-4 mr-1" /> Verileri Senkronize Et</Button>
          <Button variant="outline" size="sm" onClick={handleSyncSendGrid}><Shield className="w-4 h-4 mr-1" /> SendGrid Sync</Button>
          <Button variant="outline" size="sm" onClick={handleSnapshot}><Shield className="w-4 h-4 mr-1" /> Snapshot</Button>
          <Button variant="outline" size="sm" onClick={handleRecovery}><Zap className="w-4 h-4 mr-1" /> Recovery Check</Button>
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); load(); }}><RefreshCw className="w-4 h-4 mr-1" /></Button>
        </div>
      </div>

      {/* Critical Alerts */}
      {m.critical_alerts > 0 && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-5 h-5 text-red-500" /><span className="font-bold text-red-400">{m.critical_alerts} Kritik Uyari</span></div>
          {m.alerts?.filter(a => a.level === 'critical').map((a, i) => <p key={i} className="text-sm text-red-300 ml-7">{a.domain}: {a.message}</p>)}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { label: 'Aktif Kampanya', value: m.active_campaigns || 0, icon: Activity, color: 'text-green-500' },
          { label: 'Son 24s Gonderim', value: m.sends_24h || 0, icon: Zap, color: 'text-blue-400' },
          { label: 'Son 24s Bounce', value: m.bounces_24h || 0, icon: XCircle, color: m.bounces_24h > 0 ? 'text-red-400' : 'text-muted-foreground' },
          { label: 'Son 24s Spam', value: m.spam_24h || 0, icon: AlertTriangle, color: m.spam_24h > 0 ? 'text-red-400' : 'text-muted-foreground' },
          { label: 'Suppressed', value: m.suppression_size || 0, icon: Shield, color: 'text-purple-400' },
          { label: 'Karantina', value: m.quarantined_domains || 0, icon: Globe, color: m.quarantined_domains > 0 ? 'text-red-400' : 'text-green-500' },
        ].map((s, i) => (
          <div key={i} className="rounded-lg bg-card p-4 text-center">
            <s.icon className={`w-5 h-5 mx-auto mb-1 ${s.color}`} />
            <p className="text-2xl font-bold">{s.value}</p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Reputation Summary */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Reputation Ozeti</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-2 text-center mb-4">
              <div className="p-2 rounded bg-emerald-500/10"><p className="text-xl font-bold text-emerald-400">{r.elite || 0}</p><p className="text-[10px] text-muted-foreground">Elite</p></div>
              <div className="p-2 rounded bg-green-500/10"><p className="text-xl font-bold text-green-500">{r.healthy || 0}</p><p className="text-[10px] text-muted-foreground">Healthy</p></div>
              <div className="p-2 rounded bg-amber-500/10"><p className="text-xl font-bold text-amber-500">{r.warning || 0}</p><p className="text-[10px] text-muted-foreground">Warning</p></div>
              <div className="p-2 rounded bg-red-500/10"><p className="text-xl font-bold text-red-500">{r.critical || 0}</p><p className="text-[10px] text-muted-foreground">Critical</p></div>
            </div>
            <div className="text-sm text-muted-foreground text-center">Ortalama Health: <span className="font-bold text-foreground">{r.avg_health || 0}</span>/100</div>
          </CardContent>
        </Card>

        {/* Deliverability Report */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Deliverability</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Risk Seviyesi</span><Badge variant={d.risk_level === 'excellent' ? 'default' : d.risk_level === 'good' ? 'default' : 'destructive'}>{d.risk_level?.toUpperCase()}</Badge></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Delivery Rate</span><span>%{d.delivery_rate || 0}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Open Rate</span><span>%{d.open_rate || 0}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Bounce Rate</span><span>%{d.bounce_rate || 0}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Spam Rate</span><span>%{d.spam_rate || 0}</span></div>
          </CardContent>
        </Card>

        {/* SendGrid Accurate Stats */}
        {m.total_stats && (
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3"><CardTitle className="text-base">Toplam Istatistikler (SendGrid)</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-2 text-center">
                {[
                  { label: 'Processed', value: m.total_stats.processed, color: '' },
                  { label: 'Delivered', value: m.total_stats.delivered, color: 'text-green-500' },
                  { label: 'Opened', value: m.total_stats.opened, color: 'text-blue-400' },
                  { label: 'Clicked', value: m.total_stats.clicked, color: 'text-purple-400' },
                  { label: 'Bounced', value: m.total_stats.bounced, color: 'text-red-400' },
                  { label: 'Dropped', value: m.total_stats.dropped, color: 'text-amber-500' },
                  { label: 'Deferred', value: m.total_stats.deferred, color: 'text-orange-400' },
                  { label: 'Spam', value: m.total_stats.spam, color: 'text-red-500' },
                ].map((s, i) => (
                  <div key={i} className="p-2 rounded bg-muted/30">
                    <p className={`text-lg font-bold ${s.color}`}>{(s.value || 0).toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground">{s.label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alerts */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3"><CardTitle className="text-base">Uyarilar ({m.alert_count || 0})</CardTitle></CardHeader>
          <CardContent>
            {m.alerts?.length > 0 ? (
              <div className="space-y-2">{m.alerts.map((a, i) => (
                <div key={i} className={`flex items-center gap-2 p-2 rounded text-sm ${a.level === 'critical' ? 'bg-red-500/10' : a.level === 'warning' ? 'bg-amber-500/10' : 'bg-blue-500/10'}`}>
                  {a.level === 'critical' ? <XCircle className="w-4 h-4 text-red-500" /> : a.level === 'warning' ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <CheckCircle className="w-4 h-4 text-blue-400" />}
                  <span className="flex-1">{a.domain}: {a.message}</span>
                  <Badge variant="outline" className="text-[10px]">{a.action}</Badge>
                </div>
              ))}</div>
            ) : (
              <div className="text-center py-6"><CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" /><p className="text-sm text-muted-foreground">Sistem saglıklı, uyari yok</p></div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
